import { useEffect, useState } from "react";
import AdminLayout, { PageHeader } from "../../components/admin/AdminLayout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import FileUploader from "../../components/admin/FileUploader";
import { toast } from "sonner";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DotsSixVertical } from "@phosphor-icons/react";

/**
 * Editable page content blocks. Each block is a JSON value stored at a key.
 * We expose convenient forms for the most common keys.
 */

// ── Reusable bits ─────────────────────────────────────────────────────────────
function useSortableList(initial) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor));
  const [items, setItems] = useState(initial || []);
  const add = (template = {}) => setItems((prev) => [{ _id: `it-${Date.now()}`, ...template }, ...prev]);
  const update = (id, k, v) => setItems((prev) => prev.map((it) => it._id === id ? { ...it, [k]: v } : it));
  const remove = (id) => setItems((prev) => prev.filter((it) => it._id !== id));
  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setItems((prev) => arrayMove(prev, prev.findIndex((it) => it._id === active.id), prev.findIndex((it) => it._id === over.id)));
  };
  return { items, setItems, add, update, remove, onDragEnd, sensors };
}

function withIds(arr) {
  return (arr || []).map((it, i) => ({ _id: it._id || `it-${Date.now()}-${i}`, ...it }));
}

function SortableRow({ id, children, className = "" }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <div ref={setNodeRef} style={style} className={`relative border border-[#E2E8F0] rounded-md p-4 bg-white ${className}`} data-testid={`row-${id}`}>
      <button type="button" {...attributes} {...listeners} className="absolute left-2 top-3 cursor-grab active:cursor-grabbing text-[#4B5563] hover:text-[#C2410C] touch-none" title="Drag to reorder">
        <DotsSixVertical size={18} weight="bold" />
      </button>
      <div className="pl-8">{children}</div>
    </div>
  );
}


function HomeAboutEditor() {
  const KEY = "home.about";
  const [value, setValue] = useState({ heading: "", body: "", image_url: "" });
  useEffect(() => {
    api.get(`/admin/pages/${KEY}`).then((r) => {
      if (r.data?.value && Object.keys(r.data.value).length) setValue({ heading: "", body: "", image_url: "", ...r.data.value });
    });
  }, []);
  const save = async () => {
    try { await api.put("/admin/pages", { key: KEY, value }); toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="space-y-2"><Label>Heading</Label><Input value={value.heading} onChange={(e) => setValue({ ...value, heading: e.target.value })} data-testid="home-about-heading" /></div>
      <div className="space-y-2"><Label>Body</Label><Textarea rows={5} value={value.body} onChange={(e) => setValue({ ...value, body: e.target.value })} data-testid="home-about-body" /></div>
      <FileUploader kind="image" accept="image/*" label="Image" currentUrl={value.image_url} onUploaded={(url) => setValue({ ...value, image_url: url })} testId="home-about-image" />
      <Button onClick={save} data-testid="save-home-about" className="bg-[#C2410C] hover:bg-[#9A3412]">Save</Button>
    </div>
  );
}

function HomeAchievementsEditor() {
  const KEY = "home.achievements";
  const [value, setValue] = useState({ heading: "Our Achievements", items: [] });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor));

  useEffect(() => {
    api.get(`/admin/pages/${KEY}`).then((r) => {
      if (r.data?.value && Object.keys(r.data.value).length) {
        const v = { heading: "Our Achievements", items: [], ...r.data.value };
        // Ensure each item has stable id for DnD
        v.items = (v.items || []).map((it, i) => ({ _id: it._id || `ach-${Date.now()}-${i}`, ...it }));
        setValue(v);
      }
    });
  }, []);

  const addItem = () => setValue({ ...value, items: [{ _id: `ach-${Date.now()}`, metric: "", label: "" }, ...value.items] });
  const updateItem = (id, k, v) => {
    setValue({ ...value, items: value.items.map((it) => it._id === id ? { ...it, [k]: v } : it) });
  };
  const removeItem = (id) => setValue({ ...value, items: value.items.filter((it) => it._id !== id) });
  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = value.items.findIndex((it) => it._id === active.id);
    const newIdx = value.items.findIndex((it) => it._id === over.id);
    setValue({ ...value, items: arrayMove(value.items, oldIdx, newIdx) });
  };

  const save = async () => {
    try { await api.put("/admin/pages", { key: KEY, value }); toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2"><Label>Section heading</Label><Input value={value.heading} onChange={(e) => setValue({ ...value, heading: e.target.value })} data-testid="ach-heading" /></div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={value.items.map((it) => it._id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {value.items.map((it) => <AchievementRow key={it._id} item={it} updateItem={updateItem} removeItem={removeItem} />)}
          </div>
        </SortableContext>
      </DndContext>
      <Button variant="outline" onClick={addItem} data-testid="ach-add">+ Add achievement</Button>
      <p className="text-xs text-[#4B5563]">Drag the handle to reorder. New entries are inserted at the top.</p>
      <Button onClick={save} data-testid="save-achievements" className="bg-[#C2410C] hover:bg-[#9A3412]">Save</Button>
    </div>
  );
}

function AchievementRow({ item, updateItem, removeItem }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item._id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="grid grid-cols-12 gap-2 items-end" data-testid={`ach-row-${item._id}`}>
      <button type="button" {...attributes} {...listeners} className="col-span-1 mb-2 cursor-grab active:cursor-grabbing text-[#4B5563] hover:text-[#C2410C] touch-none" title="Drag to reorder"><DotsSixVertical size={18} weight="bold" /></button>
      <div className="col-span-3 space-y-1"><Label className="text-xs">Metric</Label><Input value={item.metric} onChange={(e) => updateItem(item._id, "metric", e.target.value)} placeholder="200+" data-testid={`ach-metric-${item._id}`} /></div>
      <div className="col-span-7 space-y-1"><Label className="text-xs">Label</Label><Input value={item.label} onChange={(e) => updateItem(item._id, "label", e.target.value)} placeholder="Products globally" data-testid={`ach-label-${item._id}`} /></div>
      <Button variant="outline" onClick={() => removeItem(item._id)} className="col-span-1" data-testid={`ach-remove-${item._id}`}>×</Button>
    </div>
  );
}

function AboutPageEditor() {
  const KEY = "about.main";
  const [value, setValue] = useState({ who_we_are: "", mission: "", vision: "", rnd: "", regulatory: "", why_us: "" });
  useEffect(() => {
    api.get(`/admin/pages/${KEY}`).then((r) => {
      if (r.data?.value && Object.keys(r.data.value).length)
        setValue({ who_we_are: "", mission: "", vision: "", rnd: "", regulatory: "", why_us: "", ...r.data.value });
    });
  }, []);
  const save = async () => {
    try { await api.put("/admin/pages", { key: KEY, value }); toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const fields = [
    ["who_we_are", "Who we are"],
    ["mission", "Mission"],
    ["vision", "Vision"],
    ["rnd", "Research & Development"],
    ["regulatory", "Regulatory Services"],
    ["why_us", "Why choose us"],
  ];
  return (
    <div className="space-y-4">
      {fields.map(([k, l]) => (
        <div key={k} className="space-y-2">
          <Label>{l}</Label>
          <Textarea rows={4} value={value[k]} onChange={(e) => setValue({ ...value, [k]: e.target.value })} data-testid={`about-${k}`} />
        </div>
      ))}
      <Button onClick={save} data-testid="save-about" className="bg-[#C2410C] hover:bg-[#9A3412]">Save</Button>
    </div>
  );
}

function MVVEditor() {
  const KEY = "about.mvv";
  const empty = { mission: "", vision: "", values: "" };
  const [value, setValue] = useState(empty);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get(`/admin/pages/${KEY}`).then((r) => {
      if (r.data?.value && Object.keys(r.data.value).length) setValue({ ...empty, ...r.data.value });
      setLoaded(true);
    });
  }, []);
  const save = async () => {
    try { await api.put("/admin/pages", { key: KEY, value }); toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!loaded) return <div className="text-sm text-[#64748B]">Loading…</div>;
  const fields = [
    ["mission", "Mission", "To make safe, effective and affordable medicines accessible to every clinician — and every patient they serve."],
    ["vision",  "Vision",  "To be the most trusted Indian pharmaceutical partner of choice for clinicians by 2030."],
    ["values",  "Values",  "Patients first. Science always. Promises kept."],
  ];
  return (
    <div className="space-y-4">
      <p className="text-xs text-[#4B5563]">Edits the three "Mission · Vision · Values" cards on the public About page. Leave a field blank to fall back to the built-in default text.</p>
      {fields.map(([k, label, placeholder]) => (
        <div key={k} className="space-y-2">
          <Label>{label}</Label>
          <Textarea rows={3} value={value[k] || ""} onChange={(e) => setValue({ ...value, [k]: e.target.value })} placeholder={placeholder} data-testid={`mvv-${k}`} />
        </div>
      ))}
      <Button onClick={save} data-testid="save-mvv" className="bg-[#C2410C] hover:bg-[#9A3412]">Save mission/vision/values</Button>
    </div>
  );
}


function PillarsEditor() {
  const KEY = "about.pillars";
  const list = useSortableList([]);
  const [loaded, setLoaded] = useState(false);

  const ICON_CHOICES = ["Flask", "ShieldCheck", "Heartbeat", "Leaf", "Sparkle", "Target", "Eye", "HandHeart", "Certificate"];

  useEffect(() => {
    api.get(`/admin/pages/${KEY}`).then((r) => {
      list.setItems(withIds(r.data?.value?.items));
    }).finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    const cleaned = list.items.map(({ _id, ...rest }) => rest);
    try { await api.put("/admin/pages", { key: KEY, value: { items: cleaned } }); toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!loaded) return <div className="text-sm text-[#64748B]">Loading…</div>;

  return (
    <div className="space-y-4">
      <p className="text-xs text-[#4B5563]">Edits the four "Built on four pillars" cards on the About page. Pick an icon name from the dropdown (see the icon names listed above each field).</p>
      <DndContext sensors={list.sensors} collisionDetection={closestCenter} onDragEnd={list.onDragEnd}>
        <SortableContext items={list.items.map((it) => it._id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {list.items.map((p) => (
              <SortableRow key={p._id} id={p._id}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Icon</Label>
                    <select
                      value={p.icon || "Flask"}
                      onChange={(e) => list.update(p._id, "icon", e.target.value)}
                      className="w-full px-3 py-2 rounded-md border border-[#E2E8F0] text-sm bg-white focus:border-[#C2410C] focus:outline-none"
                      data-testid={`pillar-icon-${p._id}`}
                    >
                      {ICON_CHOICES.map((i) => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2 space-y-1"><Label className="text-xs">Title</Label><Input value={p.title || ""} onChange={(e) => list.update(p._id, "title", e.target.value)} placeholder="Research-led" data-testid={`pillar-title-${p._id}`} /></div>
                  <div className="md:col-span-3 space-y-1"><Label className="text-xs">Text</Label><Textarea rows={2} value={p.text || ""} onChange={(e) => list.update(p._id, "text", e.target.value)} placeholder="An in-house R&D team that has filed 40+ formulation dossiers in the last decade." data-testid={`pillar-text-${p._id}`} /></div>
                </div>
                <Button variant="outline" size="sm" onClick={() => list.remove(p._id)} className="mt-3 text-[#EF4444]" data-testid={`pillar-remove-${p._id}`}>Remove pillar</Button>
              </SortableRow>
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <Button variant="outline" onClick={() => list.add({ icon: "Flask", title: "", text: "" })} data-testid="pillar-add">+ Add pillar</Button>
      <Button onClick={save} data-testid="save-pillars" className="bg-[#C2410C] hover:bg-[#9A3412]">Save pillars</Button>
    </div>
  );
}

// ── Hero banners (hero.about / hero.products / hero.blogs / hero.careers / hero.contact)
const HERO_PAGES = [
  { key: "about",    label: "About",    defaults: { eyebrow: "About us",       title: "Two decades of building medicines that matter", subtitle: "From research and formulation to manufacturing and distribution — discover the team and values behind Bonheur Pharma." } },
  { key: "products", label: "Products", defaults: { eyebrow: "Our portfolio",   title: "Pharmaceutical products built on science",       subtitle: "A growing portfolio of formulations developed for safety, efficacy, and real-world clinical impact." } },
  { key: "blogs",    label: "Insights", defaults: { eyebrow: "Insights",        title: "Science, stories and pharma perspectives",       subtitle: "Stay current with research notes, clinical updates and behind-the-scenes from our labs." } },
  { key: "careers",  label: "Careers",  defaults: { eyebrow: "Careers",         title: "Build the medicines of tomorrow with us",        subtitle: "Join a team where rigour, curiosity, and patient impact go hand in hand." } },
  { key: "contact",  label: "Contact",  defaults: { eyebrow: "Contact",         title: "Get in touch",                                   subtitle: "Whether you're a clinician, distributor or a curious researcher — we'd love to hear from you." } },
];

function HeroBannerEditor() {
  const [active, setActive] = useState(HERO_PAGES[0].key);
  return (
    <div className="space-y-4">
      <p className="text-xs text-[#4B5563]">Hero banners shown at the top of each public page (everything except the home page, which has its own slider). Leave any field blank to keep the built-in default text.</p>
      <div className="flex flex-wrap gap-1.5 p-1 bg-[#F1F5F9] rounded-lg border border-[#E2E8F0]">
        {HERO_PAGES.map((p) => (
          <button
            key={p.key}
            onClick={() => setActive(p.key)}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${active === p.key ? "bg-white text-[#C2410C] shadow-sm" : "text-[#475569] hover:text-[#C2410C]"}`}
            data-testid={`hero-page-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {HERO_PAGES.map((p) => active === p.key && <HeroPageBlock key={p.key} cfg={p} />)}
    </div>
  );
}

function HeroPageBlock({ cfg }) {
  const KEY = `hero.${cfg.key}`;
  const [value, setValue] = useState({ eyebrow: "", title: "", subtitle: "", banner_url: "" });
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get(`/admin/pages/${KEY}`).then((r) => {
      if (r.data?.value) setValue({ eyebrow: "", title: "", subtitle: "", banner_url: "", ...r.data.value });
    }).finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [KEY]);
  const save = async () => {
    try { await api.put("/admin/pages", { key: KEY, value }); toast.success(`Saved ${cfg.label} hero`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!loaded) return <div className="text-sm text-[#64748B]">Loading…</div>;
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label>Eyebrow (small label above the title)</Label>
        <Input value={value.eyebrow} onChange={(e) => setValue({ ...value, eyebrow: e.target.value })} placeholder={cfg.defaults.eyebrow} data-testid={`hero-${cfg.key}-eyebrow`} />
      </div>
      <div className="space-y-1.5">
        <Label>Title (main heading)</Label>
        <Input value={value.title} onChange={(e) => setValue({ ...value, title: e.target.value })} placeholder={cfg.defaults.title} data-testid={`hero-${cfg.key}-title`} />
      </div>
      <div className="space-y-1.5">
        <Label>Subtitle</Label>
        <Textarea rows={3} value={value.subtitle} onChange={(e) => setValue({ ...value, subtitle: e.target.value })} placeholder={cfg.defaults.subtitle} data-testid={`hero-${cfg.key}-subtitle`} />
      </div>
      <FileUploader
        kind="image"
        accept="image/*"
        label="Banner background image (optional)"
        currentUrl={value.banner_url}
        onUploaded={(url) => setValue({ ...value, banner_url: url })}
        testId={`hero-${cfg.key}-banner`}
      />
      <p className="text-xs text-[#4B5563]">When uploaded, the image sits behind a dark overlay. Without an image, the hero uses the navy → sky-blue gradient.</p>
      <Button onClick={save} data-testid={`save-hero-${cfg.key}`} className="bg-[#C2410C] hover:bg-[#9A3412]">Save {cfg.label} hero</Button>
    </div>
  );
}


function CapabilityEditor() {
  const KEY = "home.capability";
  const [value, setValue] = useState({ heading: "Our Capability", body: "", videos: [] });

  useEffect(() => {
    api.get(`/admin/pages/${KEY}`).then((r) => {
      if (r.data?.value && Object.keys(r.data.value).length) {
        const v = { heading: "Our Capability", body: "", videos: [], ...r.data.value };
        // back-compat: if old single video_url exists and videos empty, migrate it
        if (v.video_url && (!v.videos || !v.videos.length)) {
          v.videos = [{ _id: `vid-${Date.now()}`, title: "Capability", url: v.video_url, thumbnail: "" }];
        }
        v.videos = (v.videos || []).map((it, i) => ({ _id: it._id || `vid-${Date.now()}-${i}`, title: it.title || "", url: it.url || "", thumbnail: it.thumbnail || "" }));
        setValue(v);
      }
    });
  }, []);

  const addVideo = () => setValue({ ...value, videos: [{ _id: `vid-${Date.now()}`, title: "", url: "", thumbnail: "" }, ...value.videos] });
  const updateVideo = (id, k, v) => setValue({ ...value, videos: value.videos.map((it) => it._id === id ? { ...it, [k]: v } : it) });
  const removeVideo = (id) => setValue({ ...value, videos: value.videos.filter((it) => it._id !== id) });

  const save = async () => {
    try { await api.put("/admin/pages", { key: KEY, value }); toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2"><Label>Heading</Label><Input value={value.heading} onChange={(e) => setValue({ ...value, heading: e.target.value })} data-testid="cap-heading" /></div>
      <div className="space-y-2"><Label>Body</Label><Textarea rows={4} value={value.body} onChange={(e) => setValue({ ...value, body: e.target.value })} data-testid="cap-body" /></div>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Video gallery</Label>
          <Button variant="outline" size="sm" onClick={addVideo} data-testid="cap-add-video">+ Add video</Button>
        </div>
        {value.videos.length === 0 && <p className="text-xs text-[#4B5563]">No videos yet. Add one to populate the homepage capability section.</p>}
        {value.videos.map((v) => (
          <div key={v._id} className="border border-[#E2E8F0] rounded-md p-3 space-y-2" data-testid={`cap-video-${v._id}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="space-y-1"><Label className="text-xs">Title</Label><Input value={v.title} onChange={(e) => updateVideo(v._id, "title", e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs">Video URL (mp4 / YouTube embed)</Label><Input value={v.url} onChange={(e) => updateVideo(v._id, "url", e.target.value)} placeholder="/api/uploads/... or https://youtube.com/embed/..." /></div>
            </div>
            <FileUploader kind="video" accept="video/*" label="Or upload" currentUrl={v.url} onUploaded={(url) => updateVideo(v._id, "url", url)} testId={`cap-upload-${v._id}`} />
            <FileUploader kind="image" accept="image/*" label="Thumbnail (optional)" currentUrl={v.thumbnail} onUploaded={(url) => updateVideo(v._id, "thumbnail", url)} testId={`cap-thumb-${v._id}`} />
            <Button variant="outline" size="sm" onClick={() => removeVideo(v._id)} className="text-[#EF4444]">Remove</Button>
          </div>
        ))}
      </div>
      <Button onClick={save} data-testid="save-capability" className="bg-[#C2410C] hover:bg-[#9A3412]">Save</Button>
    </div>
  );
}

// ── Offices (home.offices) ───────────────────────────────────────────────────
function OfficesEditor() {
  const KEY = "home.offices";
  const list = useSortableList([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`/admin/pages/${KEY}`).then((r) => {
      list.setItems(withIds(r.data?.value?.offices));
    }).finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    // Ensure only one head office
    const cleaned = list.items.map(({ _id, ...rest }) => rest);
    try { await api.put("/admin/pages", { key: KEY, value: { offices: cleaned } }); toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleHead = (id) => {
    list.setItems((prev) => prev.map((o) => ({ ...o, is_head_office: o._id === id })));
  };

  if (!loaded) return <div className="text-sm text-[#64748B]">Loading…</div>;

  return (
    <div className="space-y-4">
      <DndContext sensors={list.sensors} collisionDetection={closestCenter} onDragEnd={list.onDragEnd}>
        <SortableContext items={list.items.map((it) => it._id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {list.items.map((o) => (
              <SortableRow key={o._id} id={o._id}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <Input value={o.name || ""} onChange={(e) => list.update(o._id, "name", e.target.value)} placeholder="Office name e.g. Mumbai Branch Office" className="font-semibold" data-testid={`office-name-${o._id}`} />
                  <label className="flex items-center gap-2 whitespace-nowrap text-xs font-semibold cursor-pointer">
                    <input type="checkbox" checked={!!o.is_head_office} onChange={() => toggleHead(o._id)} className="rounded" data-testid={`office-head-${o._id}`} />
                    Head office
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2 space-y-1">
                    <Label className="text-xs">Address (multi-line)</Label>
                    <Textarea rows={3} value={o.address || ""} onChange={(e) => list.update(o._id, "address", e.target.value)} placeholder="Plot 14B, Sector 5 Industrial Area&#10;Baddi — 173205&#10;Himachal Pradesh, India" data-testid={`office-address-${o._id}`} />
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Phone</Label><Input value={o.phone || ""} onChange={(e) => list.update(o._id, "phone", e.target.value)} placeholder="+91 1795 244 200" data-testid={`office-phone-${o._id}`} /></div>
                  <div className="space-y-1"><Label className="text-xs">Email</Label><Input value={o.email || ""} onChange={(e) => list.update(o._id, "email", e.target.value)} placeholder="info@bonheurpharma.in" data-testid={`office-email-${o._id}`} /></div>
                  <div className="md:col-span-2 space-y-1"><Label className="text-xs">Hours</Label><Input value={o.hours || ""} onChange={(e) => list.update(o._id, "hours", e.target.value)} placeholder="Mon–Sat · 9:00 AM – 6:00 PM IST" data-testid={`office-hours-${o._id}`} /></div>
                  {o.is_head_office && (
                    <div className="md:col-span-2 space-y-1">
                      <Label className="text-xs">Google Maps embed code (only for head office)</Label>
                      <Textarea rows={3} value={o.map_embed || ""} onChange={(e) => list.update(o._id, "map_embed", e.target.value)} placeholder='Paste full <iframe src="https://www.google.com/maps/embed?pb=..."></iframe>' className="font-mono text-xs" data-testid={`office-map-${o._id}`} />
                    </div>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={() => list.remove(o._id)} className="mt-3 text-[#EF4444]" data-testid={`office-remove-${o._id}`}>Remove office</Button>
              </SortableRow>
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <Button variant="outline" onClick={() => list.add({ name: "", address: "", phone: "", email: "", hours: "", is_head_office: false })} data-testid="office-add">+ Add office</Button>
      <p className="text-xs text-[#4B5563]">The &ldquo;Head office&rdquo; row drives the dedicated map embed on the Contact page. Only one office can be marked as head.</p>
      <Button onClick={save} data-testid="save-offices" className="bg-[#C2410C] hover:bg-[#9A3412]">Save offices</Button>
    </div>
  );
}


// ── Leadership (about.leadership) ────────────────────────────────────────────
function LeadershipEditor() {
  const KEY = "about.leadership";
  const list = useSortableList([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`/admin/pages/${KEY}`).then((r) => {
      list.setItems(withIds(r.data?.value?.items));
    }).finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    const cleaned = list.items.map(({ _id, ...rest }) => rest);
    try { await api.put("/admin/pages", { key: KEY, value: { items: cleaned } }); toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!loaded) return <div className="text-sm text-[#64748B]">Loading…</div>;

  return (
    <div className="space-y-4">
      <DndContext sensors={list.sensors} collisionDetection={closestCenter} onDragEnd={list.onDragEnd}>
        <SortableContext items={list.items.map((it) => it._id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {list.items.map((p) => (
              <SortableRow key={p._id} id={p._id}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Name</Label><Input value={p.name || ""} onChange={(e) => list.update(p._id, "name", e.target.value)} placeholder="Rajiv Krishnan" data-testid={`lead-name-${p._id}`} /></div>
                  <div className="space-y-1"><Label className="text-xs">Role / Title</Label><Input value={p.role || ""} onChange={(e) => list.update(p._id, "role", e.target.value)} placeholder="Founder & Managing Director" data-testid={`lead-role-${p._id}`} /></div>
                  <div className="md:col-span-2 space-y-1"><Label className="text-xs">Bio</Label><Textarea rows={4} value={p.bio || ""} onChange={(e) => list.update(p._id, "bio", e.target.value)} placeholder="Two decades of operating experience across formulations, regulatory affairs and commercial leadership. Full bio shown in the About-page popup." data-testid={`lead-bio-${p._id}`} /></div>
                  <div className="space-y-1"><Label className="text-xs">Email (optional)</Label><Input value={p.email || ""} onChange={(e) => list.update(p._id, "email", e.target.value)} placeholder="name@bonheurpharma.com" data-testid={`lead-email-${p._id}`} /></div>
                  <div className="space-y-1"><Label className="text-xs">LinkedIn URL (optional)</Label><Input value={p.linkedin || ""} onChange={(e) => list.update(p._id, "linkedin", e.target.value)} placeholder="https://linkedin.com/in/..." data-testid={`lead-linkedin-${p._id}`} /></div>
                  <div className="md:col-span-2">
                    <FileUploader kind="image" accept="image/*" label="Photo" currentUrl={p.image_url} onUploaded={(url) => list.update(p._id, "image_url", url)} testId={`lead-photo-${p._id}`} />
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => list.remove(p._id)} className="mt-3 text-[#EF4444]" data-testid={`lead-remove-${p._id}`}>Remove</Button>
              </SortableRow>
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <Button variant="outline" onClick={() => list.add({ name: "", role: "", bio: "", image_url: "" })} data-testid="lead-add">+ Add team member</Button>
      <p className="text-xs text-[#4B5563]">Order here controls the order on the About page. Drag to reorder.</p>
      <Button onClick={save} data-testid="save-leadership" className="bg-[#C2410C] hover:bg-[#9A3412]">Save leadership</Button>
    </div>
  );
}


// ── Milestones (about.milestones) ────────────────────────────────────────────
function MilestonesEditor() {
  const KEY = "about.milestones";
  const list = useSortableList([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`/admin/pages/${KEY}`).then((r) => {
      list.setItems(withIds(r.data?.value?.items));
    }).finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    const cleaned = list.items.map(({ _id, ...rest }) => rest);
    try { await api.put("/admin/pages", { key: KEY, value: { items: cleaned } }); toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!loaded) return <div className="text-sm text-[#64748B]">Loading…</div>;

  return (
    <div className="space-y-4">
      <DndContext sensors={list.sensors} collisionDetection={closestCenter} onDragEnd={list.onDragEnd}>
        <SortableContext items={list.items.map((it) => it._id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {list.items.map((m) => (
              <SortableRow key={m._id} id={m._id}>
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-12 md:col-span-2 space-y-1"><Label className="text-xs">Year</Label><Input value={m.year || ""} onChange={(e) => list.update(m._id, "year", e.target.value)} placeholder="2003" data-testid={`mile-year-${m._id}`} /></div>
                  <div className="col-span-12 md:col-span-10 space-y-1"><Label className="text-xs">Title</Label><Input value={m.title || ""} onChange={(e) => list.update(m._id, "title", e.target.value)} placeholder="Founded in Baddi" data-testid={`mile-title-${m._id}`} /></div>
                  <div className="col-span-12 space-y-1"><Label className="text-xs">Description</Label><Textarea rows={2} value={m.text || ""} onChange={(e) => list.update(m._id, "text", e.target.value)} placeholder="Started with a single tablet line and 22 employees." data-testid={`mile-text-${m._id}`} /></div>
                </div>
                <Button variant="outline" size="sm" onClick={() => list.remove(m._id)} className="mt-3 text-[#EF4444]" data-testid={`mile-remove-${m._id}`}>Remove milestone</Button>
              </SortableRow>
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <Button variant="outline" onClick={() => list.add({ year: "", title: "", text: "" })} data-testid="mile-add">+ Add milestone</Button>
      <p className="text-xs text-[#4B5563]">Listed in this order on the About page timeline. Drag to reorder.</p>
      <Button onClick={save} data-testid="save-milestones" className="bg-[#C2410C] hover:bg-[#9A3412]">Save milestones</Button>
    </div>
  );
}


// ── Certifications (about.certifications) ────────────────────────────────────
function CertificationsEditor() {
  const KEY = "about.certifications";
  const list = useSortableList([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`/admin/pages/${KEY}`).then((r) => {
      list.setItems(withIds(r.data?.value?.items));
    }).finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    const cleaned = list.items.map(({ _id, ...rest }) => rest);
    try { await api.put("/admin/pages", { key: KEY, value: { items: cleaned } }); toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!loaded) return <div className="text-sm text-[#64748B]">Loading…</div>;

  return (
    <div className="space-y-4">
      <DndContext sensors={list.sensors} collisionDetection={closestCenter} onDragEnd={list.onDragEnd}>
        <SortableContext items={list.items.map((it) => it._id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {list.items.map((c) => (
              <SortableRow key={c._id} id={c._id}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Name</Label><Input value={c.name || ""} onChange={(e) => list.update(c._id, "name", e.target.value)} placeholder="WHO-GMP" data-testid={`cert-name-${c._id}`} /></div>
                  <div className="space-y-1"><Label className="text-xs">Issuer</Label><Input value={c.issuer || ""} onChange={(e) => list.update(c._id, "issuer", e.target.value)} placeholder="World Health Organization" data-testid={`cert-issuer-${c._id}`} /></div>
                  <div className="space-y-1"><Label className="text-xs">Year / Status</Label><Input value={c.year || ""} onChange={(e) => list.update(c._id, "year", e.target.value)} placeholder="Renewed 2024" data-testid={`cert-year-${c._id}`} /></div>
                </div>
                <Button variant="outline" size="sm" onClick={() => list.remove(c._id)} className="mt-3 text-[#EF4444]" data-testid={`cert-remove-${c._id}`}>Remove</Button>
              </SortableRow>
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <Button variant="outline" onClick={() => list.add({ name: "", issuer: "", year: "" })} data-testid="cert-add">+ Add certification</Button>
      <Button onClick={save} data-testid="save-certifications" className="bg-[#C2410C] hover:bg-[#9A3412]">Save certifications</Button>
    </div>
  );
}


// ── Marquee (home.marquee) ──────────────────────────────────────────────────
function MarqueeEditor() {
  const KEY = "home.marquee";
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    api.get(`/admin/pages/${KEY}`).then((r) => {
      const arr = r.data?.value?.items;
      setItems(Array.isArray(arr) ? arr : []);
    });
  }, []);

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    setItems([...items, v]);
    setDraft("");
  };
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
  };
  const save = async () => {
    try {
      await api.put("/admin/pages", { key: KEY, value: { items } });
      toast.success("Marquee saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="marquee-editor">
      <p className="text-xs text-[#4B5563]">
        Certifications and accreditations that scroll across the homepage ribbon.
        Add short phrases — keep them under 40 characters for the best look.
      </p>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="e.g. WHO-GMP  ·  ISO 9001:2015  ·  1000+ Formulations"
          data-testid="marquee-input"
        />
        <Button variant="outline" onClick={add} data-testid="marquee-add">Add</Button>
      </div>
      {items.length === 0 && (
        <p className="text-xs text-[#4B5563]">No items yet. The ribbon will use the default list until you add one.</p>
      )}
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={`${i}-${it}`} className="flex items-center gap-2 bg-white border border-[#E2E8F0] rounded-md px-3 py-2 text-sm" data-testid={`marquee-item-${i}`}>
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#4B5563] w-6">{String(i + 1).padStart(2, "0")}</span>
            <span className="flex-1 truncate text-[#1F2937]">{it}</span>
            <button onClick={() => move(i, -1)} className="text-[#4B5563] hover:text-[#C2410C] px-1" title="Move up">↑</button>
            <button onClick={() => move(i, +1)} className="text-[#4B5563] hover:text-[#C2410C] px-1" title="Move down">↓</button>
            <button onClick={() => remove(i)} className="text-[#4B5563] hover:text-[#EF4444] px-1" title="Remove" data-testid={`marquee-remove-${i}`}>×</button>
          </li>
        ))}
      </ul>
      <Button onClick={save} data-testid="save-marquee" className="bg-[#C2410C] hover:bg-[#9A3412]">Save</Button>
    </div>
  );
}


export default function Sections() {
  return (
    <AdminLayout>
      <PageHeader title="Page Content" subtitle="Editable text & content blocks for the Home, About and Contact pages." />
      <div className="p-8 max-w-3xl">
        <Tabs defaultValue="hero-banners" className="w-full">
          <TabsList className="bg-[#F7F8F6] border border-[#E2E8F0] flex flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="hero-banners" data-testid="tab-hero-banners">Page Heroes</TabsTrigger>
            <TabsTrigger value="home-about" data-testid="tab-home-about">Home → About</TabsTrigger>
            <TabsTrigger value="achievements" data-testid="tab-achievements">Home → Achievements</TabsTrigger>
            <TabsTrigger value="marquee" data-testid="tab-marquee">Home → Marquee</TabsTrigger>
            <TabsTrigger value="capability" data-testid="tab-capability">Home → Capability</TabsTrigger>
            <TabsTrigger value="mvv" data-testid="tab-mvv">About → Mission/Vision/Values</TabsTrigger>
            <TabsTrigger value="pillars" data-testid="tab-pillars">About → Pillars</TabsTrigger>
            <TabsTrigger value="milestones" data-testid="tab-milestones">About → Milestones</TabsTrigger>
            <TabsTrigger value="leadership" data-testid="tab-leadership">About → Leadership</TabsTrigger>
            <TabsTrigger value="certifications" data-testid="tab-certifications">About → Certifications</TabsTrigger>
            <TabsTrigger value="offices" data-testid="tab-offices">Contact → Offices</TabsTrigger>
          </TabsList>
          <TabsContent value="hero-banners" className="mt-6 bg-white border border-[#E2E8F0] rounded-lg p-6"><HeroBannerEditor /></TabsContent>
          <TabsContent value="home-about" className="mt-6 bg-white border border-[#E2E8F0] rounded-lg p-6"><HomeAboutEditor /></TabsContent>
          <TabsContent value="achievements" className="mt-6 bg-white border border-[#E2E8F0] rounded-lg p-6"><HomeAchievementsEditor /></TabsContent>
          <TabsContent value="marquee" className="mt-6 bg-white border border-[#E2E8F0] rounded-lg p-6"><MarqueeEditor /></TabsContent>
          <TabsContent value="capability" className="mt-6 bg-white border border-[#E2E8F0] rounded-lg p-6"><CapabilityEditor /></TabsContent>
          <TabsContent value="mvv" className="mt-6 bg-white border border-[#E2E8F0] rounded-lg p-6"><MVVEditor /></TabsContent>
          <TabsContent value="pillars" className="mt-6 bg-white border border-[#E2E8F0] rounded-lg p-6"><PillarsEditor /></TabsContent>
          <TabsContent value="milestones" className="mt-6 bg-white border border-[#E2E8F0] rounded-lg p-6"><MilestonesEditor /></TabsContent>
          <TabsContent value="leadership" className="mt-6 bg-white border border-[#E2E8F0] rounded-lg p-6"><LeadershipEditor /></TabsContent>
          <TabsContent value="certifications" className="mt-6 bg-white border border-[#E2E8F0] rounded-lg p-6"><CertificationsEditor /></TabsContent>
          <TabsContent value="offices" className="mt-6 bg-white border border-[#E2E8F0] rounded-lg p-6"><OfficesEditor /></TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
